import React, { FC, useEffect, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link, useNavigate } from "react-router-dom";
import { btn_value, data_reps, more_report_data } from "./dashboard/data";
import BadgeImage from "./ui-elements/badge-image";
import { ArrowRight } from "./ui-elements/svg-icons";
import { useMutation } from "@tanstack/react-query";
import { apiGetPosts } from "../api/post";
import { useNotificationService } from "../hooks/notification";
import { PostContentProps } from "../interface/dashboard";
import { formatDateAndTimeFromString } from "../utils/date-setup";
import { PATH_DASHBOARD } from "../routes/path";

const ReportSummaryContainer = ({ post_id }: { post_id: number }) => {
  const theme = useTheme();
  const [list, setList] = useState<PostContentProps[]>([]);
  const { showNotification } = useNotificationService();

  useEffect(() => {
    mutation.mutate();
  }, []);

  const mutation = useMutation({
    mutationFn: apiGetPosts,
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("details" in data) {
          showNotification(data?.details ?? "An error occurred", "error");
          return;
        }
        return;
      }
      if (data.status === 200) {
        if (Array.isArray(data?.data?.data)) {
          const filter_data = data.data.data.filter(
            (post: PostContentProps) => post.id !== post_id
          );
          setList(filter_data);
        }
      }
    },
    onError: (error) => {
      showNotification(error?.message ?? "Serverr error", "error");
    },
  });

  return (
    <Box
      sx={{
        border: "1px solid rgba(231, 235, 239, 1)",
        mt: 2,
        borderRadius: "8px",
        px: 2,
        minHeight: 250,
        height: "100%",
        overflowY: "auto",
      }}
    >
      <Typography sx={{ fontWeight: 500, mt: 2 }} variant="body1" color="black">
        More reports
      </Typography>
      {mutation.isPending && (
        <Box
          sx={{
            width: "100%",
            height: "80%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {!mutation.isPending && list?.length > 0 && (
        <Box sx={{}}>
          {list?.map((post, id) => (
            <SummaryCover
              key={post?.id}
              postId={post?.id}
              image={post?.media}
              name={post.author}
              title={post.title}
              date={post.created_at}
              profile={post?.author_photo_url}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ReportSummaryContainer;

const SummaryCover: FC<{
  image: any;
  name: string;
  title: string;
  date: string;
  profile: string;
  postId: number;
}> = ({ image, title, name, date, profile, postId }) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        my: 3,
        mx: 2,
        cursor: "pointer",
      }}
      onClick={() =>
        navigate(PATH_DASHBOARD.post, {
          state: { postId: postId },
        })
      }
    >
      <Stack>
        <Stack direction="row" alignItems={"center"} spacing={1} sx={{ my: 1 }}>
          <img
            src={profile}
            width={24}
            height={24}
            alt=""
            style={{ borderRadius: "40px" }}
          />
          <Typography
            sx={{ color: "#7991A4", fontWeight: 500 }}
            variant="subtitle2"
          >
            {name}
          </Typography>
        </Stack>

        <Typography
          sx={{ color: "#7991A4", fontWeight: 500 }}
          variant="caption"
        >
          {formatDateAndTimeFromString(date)}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }} color="black">
          {title}
        </Typography>

        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          sx={{ my: 1 }}
        >
          <Typography variant="caption" color="primary">
            community report
          </Typography>
        </Stack>
      </Stack>
      {image?.length > 0 && (
        <Stack>
          <img
            src={image[0]}
            alt=""
            width={90}
            height={78}
            style={{ borderRadius: "4px" }}
          />
        </Stack>
      )}
    </Box>
  );
};
